import { WithdrawalTransactionDetail } from './../../../models/withdrawal-transaction.model';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { InquiryService } from '../../../services/inquiry.service';
import { Inquiry, InquiryPriority, InquiryStatus, InquiryStatusMap } from '../../../models/inquiry.model';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../material.module';
import { HttpResponse } from '@angular/common/http';
import { ModalSnackbarService } from '../../../services/modal-snackbar.service';
import { FileUtilsService } from '../../../services/file-utils.service';
import { AdminUser, AdminUsersService } from '../../../services/admin-users.service';
import { UserInfoComponent } from '../user-info/user-info.component';
import { MatAccordion } from '@angular/material/expansion';
import { blankOpen, formatAmount, formatDate, truncateDecimals } from '../../../misc/functions';
import { WithdrawalTransactionService } from '../../../services/withdrawal-transaction.service';
import { WithdrawalTransaction } from '../../../models/withdrawal-transaction.model';
import { DecimalInputDirective } from '../../common/decimal-input.directive';
import { DecimalInputComponent } from '../../common/decimal-input.component';
import { bcadd, bccomp } from '../../../misc/bcmath';

@Component({
  selector: 'app-admin-withdrawal-transaction-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MaterialModule,
    UserInfoComponent,
    DecimalInputComponent,
    DecimalInputDirective
  ],
  templateUrl: './admin-withdrawal-transaction-detail.component.html',
  styleUrls: ['./admin-withdrawal-transaction-detail.component.scss']
})
export class AdminWithdrawalTransactionDetailComponent implements OnInit {
  formatAmount = formatAmount;
  formatDate = formatDate;
  truncateDecimals = truncateDecimals;

  adminForm!: FormGroup;
  priorityOptions = InquiryPriority;

  transaction!: WithdrawalTransaction;
  form!: FormGroup;
  selectedFiles: File[] = [];

  adminUsers: AdminUser[] = [];
  isLoaded = false;
  isEditingAmount = false;

  constructor(
    private route: ActivatedRoute,
    private service: WithdrawalTransactionService,
    private fb: FormBuilder,
    private snackBar: ModalSnackbarService,
    public adminUsersService: AdminUsersService
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));

    this.adminUsersService.getAdminUsers().subscribe(res => {
      this.adminUsers = this.adminUsersService.parseData(res);
      this.isLoaded = true;
      this.load(id);
    });

    this.adminForm = this.fb.group({
      requested_amount: [''],
      notes: ['', Validators.maxLength(4096)],
    });

    this.form = this.fb.group({
      id: [0, Validators.required],
      transaction_date: [new Date(), Validators.required],
      amount_gross: [0, [Validators.required, Validators.min(1)]],
      fee: [0, Validators.required],
      amount_net: [0, [Validators.required, this.amountNetNotExceedUnpaid()]],
      transaction_ref: ['', Validators.maxLength(50)],
      remarks: ['', Validators.maxLength(400)],
    });

  }

  private amountNetNotExceedUnpaid(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {

      if (parseInt(control?.parent?.get('id')?.value) > 0) return null;

      const net = Number(control.value);
      const unpaid = Number(this.transaction?.unpaid_amount);

      if (!isFinite(net) || !isFinite(unpaid)) {
        return null; // 値が変な時はとりあえずOK扱い
      }

      return (bccomp(net, unpaid) > 0) ? { exceedUnpaid: true } : null;

      //return net > unpaid ? { exceedUnpaid: true } : null;
    };
  }

  load(id: number): void {
    this.service.get(id, true).subscribe({
      next: (res) => {
        this.transaction = res;

        this.adminForm.patchValue({
          requested_amount: formatAmount(this.transaction.requested_amount, this.transaction.currency),
          notes: this.transaction.notes
        });

        this.formReset();
        this.isEditingAmount = false;
      },
      error: () => this.snackBar.show('読み込み失敗')
    });
  }

  formReset() {
    this.form.reset({
      id: 0,
      transaction_date: new Date,
      amount_gross: 0,
      fee: 0,
      amount_net: 0,
      transaction_ref: '',
      remarks: ''
    });
  }

  setFix(detail: WithdrawalTransactionDetail) {
    this.form.patchValue({
      id: detail.id,
      transaction_date: detail.transaction_date,
      amount_gross: truncateDecimals(detail.amount_gross, this.transaction.currency),
      fee: truncateDecimals(detail.fee, this.transaction.currency),
      amount_net: truncateDecimals(detail.amount_net, this.transaction.currency),
      transaction_ref: detail.transaction_ref ?? '',
      remarks: detail.remarks ?? '',
    });
  }

  onUpdate(): void {
    if (this.adminForm.invalid) return;

    const payload = {
      requested_amount: this.adminForm.value.requested_amount.replace(/,/g, ''),
      notes: this.adminForm.value.notes
    };

    this.service.update(this.transaction.id, payload).subscribe(() => {
      // 更新完了後の処理
      this.load(this.transaction.id);
      this.snackBar.show('管理者用メモを更新しました');
    });
  }

  calc() {
    const sum = parseFloat(this.form.value.amount_gross) + parseFloat(this.form.value.fee);
    this.form.get('amount_net')?.setValue(sum, { emitEvent: false });
  }

  inquiryLink(id: number) {
    blankOpen(undefined, '/client/admin/inquiry/' + id);
  }

  submit(): void {

    if (this.form.invalid) return;

    const formData = new FormData();

    formData.append('id', this.form.value.id);
    formData.append('amount_gross', this.form.value.amount_gross);
    formData.append('fee', this.form.value.fee);
    formData.append('amount_net', this.form.value.amount_net);
    formData.append('transaction_ref', this.form.value.transaction_ref);
    formData.append('transaction_date', formatDate(this.form.value.transaction_date.toString(), true));
    formData.append('remarks', this.form.value.remarks);

    this.service.addDetail(this.transaction.id, formData).subscribe({
      next: () => {
        this.snackBar.show('出金記録を' + (this.form.value.id > 0 ? '訂正' : '追加') + 'しました');
        this.load(this.transaction.id);
      },
      error: () => {
        this.snackBar.show('作成に失敗しました');
      }
    });

  }

}

